import { Button, Flex, Icon, Input, Spinner, Table, Tbody, Td, Text, Tfoot, Th, Thead, Tooltip, Tr, useColorModeValue } from "@chakra-ui/react";
import { useKeycloak } from "@react-keycloak/web";
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import Card from 'components/card/Card';
import { useEffect, useState } from "react";
import { IoMdDocument } from "react-icons/io";
import { MdMarkEmailRead } from "react-icons/md";
import { toast } from 'react-toastify';
import { downloadAttachment, getAllPaginated } from "services/CommunicationService";
import { formatDateDDmmYYYY } from "utils/common-utils";
import { Communication, CommunicationAttachments, Customer, Filters, GetCommunicationPaginatedRs } from "utils/interfaces";

export default function CommunicationsTable({
    customer,
    isOpen,
    handleFetchDeadlines,
    setHandleFetchDeadlines
}: {
    customer: Customer,
    isOpen: boolean,
    handleFetchDeadlines: boolean,
    setHandleFetchDeadlines: (value: boolean) => void
}) {

    const columnHelper = createColumnHelper<Communication>();

    //Colors
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [inputPageNumber, setInputPageNumber] = useState('');

    const { keycloak } = useKeycloak();

    // FILTERS
    const [filters, setFilters] = useState<Filters<any>>({
        pageNumber: 1,
        customerId: customer.id,
    });

    const [communications, setCommunications] = useState<GetCommunicationPaginatedRs>();
    const [isCommunicationsLoading, setIsCommunicationsLoading] = useState(false);

    useEffect(() => {
        const fetchCommunications = async () => {
            if (isOpen || handleFetchDeadlines) {
                await getCustomerCommunicationsPaginated();
                setHandleFetchDeadlines(false);
            }
        }
        fetchCommunications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, isOpen, handleFetchDeadlines]);

    const columns = [
        columnHelper.accessor('toEmail', {
            id: 'toEmail',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    paddingInlineStart='var(--chakra-space-6)'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'>
                    Destinatari
                </Text>
            ),
            cell: (info: any) => {
                const emails = info.getValue(); // Ottieni l'array di email
                const maxLength = 50; // Soglia massima per il testo visibile
                const displayedEmails = emails.join(", "); // Unisci le email con una virgola
                const isTruncated = displayedEmails.length > maxLength; // Controlla se il testo supera la soglia
            
                return (
                    <Flex align="center">
                        {isTruncated ? (
                            <Tooltip label={displayedEmails} hasArrow>
                                <Text
                                    color={textColor}
                                    fontSize="xs"
                                    fontWeight="700"
                                    noOfLines={1} // Mostra solo una riga
                                >
                                    {displayedEmails.slice(0, maxLength)}...
                                </Text>
                            </Tooltip>
                        ) : (
                            <Text
                                color={textColor}
                                fontSize="xs"
                                fontWeight="700"
                            >
                                {displayedEmails}
                            </Text>
                        )}
                    </Flex>
                );
            }
        }),
        columnHelper.accessor('subject', {
            id: 'subject',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    paddingInlineStart='var(--chakra-space-6)'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'>
                    Oggetto
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text
                        color={textColor}
                        fontSize='xs'
                        fontWeight='700'
                    >
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('modelType', {
            id: 'modelType',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    paddingInlineStart='var(--chakra-space-6)'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'>
                    Tipo
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text
                        color={textColor}
                        fontSize='xs'
                        fontWeight='700'
                    >
                        {info.getValue().type}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('model', {
            id: 'model',
            header: () => (
                <Text
                    justifyContent='space-between'
                    align='center'
                    paddingInlineStart='var(--chakra-space-6)'
                    marginRight='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'>
                    Modello
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text
                        color={textColor}
                        fontSize='xs'
                        fontWeight='700'
                    >
                        {info.getValue().name}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('sentDate', {
            id: 'sentDate',
            header: () => (
                <Text
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    margin='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >
                    <span>Data Invio</span>
                </Text>
            ),
            cell: (info) => (
                <Flex align='center'>
                    {info.getValue() != null ? (
                        <Text textAlign="center" color={textColor} fontSize='xs' fontWeight='700' marginLeft='auto' marginRight='auto'>
                            {formatDateDDmmYYYY(info.getValue())}
                        </Text>
                    ) : (
                        <Text textAlign="center" color={textColor} fontSize='xs' fontWeight='700' marginLeft='auto' marginRight='auto'>
                            -
                        </Text>
                    )}
                </Flex>
            )
        }),
        columnHelper.accessor('attachments', {
            id: 'attachments',
            header: () => (
                <Text
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    margin='auto'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                    textAlign="center"
                >
                    <span>Allegati</span>
                </Text>
            ),
            cell: (info) => (
                <Flex align="center" justifyContent="center">
                    {info.getValue().length > 0 ? (
                        <Flex align="center" justifyContent="center" wrap="wrap" gap={0.5}>
                            {info.getValue().map((attachment: CommunicationAttachments, index: number) => (
                                <Tooltip key={index} label={`Scarica File: ${attachment.fileName}`} hasArrow>
                                    <Button
                                        variant="link"
                                        onClick={() => handleDownloadAttachment(attachment)}
                                        display="flex"
                                        minWidth={0}
                                    >
                                        <Icon as={IoMdDocument} w={6} h={6} />
                                    </Button>
                                </Tooltip>
                            ))}
                        </Flex>
                    ) : (
                        <Text textAlign="center" color={textColor} fontSize="xs" fontWeight="700">
                            -
                        </Text>
                    )}
                </Flex>
            )
        }),
        columnHelper.accessor('sent', {
            id: 'sent',
            header: () => (
                <Text
                    marginLeft='auto'
                    marginRight='auto'
                    justifyContent='center'
                    align='center'
                    fontSize={{ sm: '10px', lg: '14px' }}
                    textTransform="none"
                    color='gray.400'
                >
                    Stato Invio
                </Text>
            ),
            cell: (info: any) => (
                <Flex justifyContent='center' alignItems='center'>
                    {renderNotificationIcons(info.row.original)}
                </Flex>
            )
        })
    ];

    const table = useReactTable({
        columns,
        data: communications?.content || [],
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        debugTable: false, // Disabilita i log di debug della tabella
        debugHeaders: false, // Disabilita i log di debug degli header
        debugColumns: false  // Disabilita i log di debug delle colonne
    });

    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage <= communications.totalPages) {
            setCurrentPage(newPage);
            setFilters((prevFilters) => ({
                ...prevFilters,
                pageNumber: newPage,
            }));
        }
    };

    const getCustomerCommunicationsPaginated = async () => {
        setIsCommunicationsLoading(true);
        try {
            const communicationsRs: GetCommunicationPaginatedRs = await getAllPaginated(keycloak.token, onSessionExpired, filters);
            setCommunications(communicationsRs);
        } catch (error) {
            console.error("Errore durante il recupero delle comunicazioni:", error);
            toast.error("Errore durante il recupero delle comunicazioni.", {
                position: "bottom-right",
            });
        } finally {
            setIsCommunicationsLoading(false);
        }
    }

    const onSessionExpired = () => {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.", {
            position: "bottom-right",
            onClose: () => {
                keycloak.logout();
                localStorage.removeItem("kc-token");
            }
        });
    };

    const renderNotificationIcons = (communication: Communication): React.ReactNode => {
        const { sent, sentDate, errorOccurred, errorMessage } = communication;

        let iconColor;
        let tooltipLabel;

        if (errorOccurred) {
            // Caso di errore
            iconColor = 'var(--chakra-colors-red-500)';
            tooltipLabel = `Data di invio: ${new Date(sentDate).toLocaleString()}, Errore: ${errorMessage || "-"}`;
        } else if (sent) {
            // Caso di invio riuscito
            iconColor = 'var(--chakra-colors-green-500)';
            tooltipLabel = `Data di invio: ${new Date(sentDate).toLocaleString()}`;
        } else {
            // Caso di invio non ancora effettuato
            iconColor = 'gray.300';
            tooltipLabel = 'Non ancora inviato';
        }

        return (
            <Tooltip label={tooltipLabel} hasArrow>
                <span tabIndex={0}>
                    <Icon as={MdMarkEmailRead} w={6} h={6} color={iconColor} mx="2" />
                </span>
            </Tooltip>
        );
    };

    const handleDownloadAttachment = async (attachment: CommunicationAttachments) => {
        try {
            toast.warning("Download file in corso...", {
                position: "bottom-right",
                autoClose: 1000
            });

            const downloadUrl: string = await downloadAttachment(
                keycloak.token,
                onSessionExpired,
                attachment.s3Key
            );

            const link = document.createElement('a'); // Crea un elemento <a>
            link.href = downloadUrl;
            link.download = attachment.fileName; // Imposta il nome del file
            link.click(); // Simula un clic per avviare il download

            window.URL.revokeObjectURL(downloadUrl); // Rimuove l'URL temporaneo

            toast.success("File scaricato con successo!", {
                position: "bottom-right",
                autoClose: 1000
            });
        } catch (error) {
            console.error("Errore durante il download del file:", error);
            toast.error("Errore durante il download del file.", {
                position: "bottom-right",
            });
        }
    };

    return (
        <Card flexDirection='column' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
            <Table variant='simple' color='gray.500'>
                <Thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <Th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        borderColor={borderColor}
                                        paddingInline="0px"
                                    >
                                        <Flex
                                            justifyContent='space-between'
                                            align='center'
                                            fontSize={{ sm: '10px', lg: '12px' }}
                                            color='gray.400'>
                                            {flexRender(header.column.columnDef.header, header.getContext())}{{
                                                asc: '',
                                                desc: '',
                                            }[header.column.getIsSorted() as string] ?? null}
                                        </Flex>
                                    </Th>
                                );
                            })}
                        </Tr>
                    ))}
                </Thead>
                <Tbody>
                    {isCommunicationsLoading ? (
                        Array.from({ length: 15 }).map((_, rowIndex) => (
                            <Tr key={`loading-row-${rowIndex}`}>
                                {columns.map((column, columnIndex) => (
                                    <Td
                                        key={`loading-cell-${rowIndex}-${columnIndex}`}
                                        pb='6px'
                                        fontSize={{ sm: '14px' }}
                                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                        borderColor='transparent'
                                        textAlign="center"
                                    >
                                        {rowIndex === 0 ? <Spinner size="sm" color="gray.500" /> : null}
                                    </Td>
                                ))}
                            </Tr>
                        ))
                    ) : communications?.content?.length > 0 ? (
                        <>
                            {table.getRowModel().rows.map((row) => (
                                <Tr
                                    key={row.id}
                                    _hover={{ bg: 'gray.200' }}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <Td
                                            pb='2px'
                                            key={cell.id}
                                            fontSize={{ sm: '14px' }}
                                            minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                            borderColor='transparent'
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    ))}
                                </Tr>
                            ))}
                        </>
                    ) : (
                        <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                                Nessuna comunicazione
                            </Td>
                        </Tr>
                    )}
                </Tbody>
                <Tfoot w="100%">
                    <Tr>
                        <Td colSpan={8} textAlign="center">
                            <Flex justifyContent="center" alignItems="center" mt={4}>
                                <Button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    isDisabled={communications?.pageable?.pageNumber === 0}
                                    height="100%"
                                >
                                    Precedente
                                </Button>
                                <Text mx={2}>
                                    Pagina {communications ? communications.pageable?.pageNumber + 1 : 0} di {communications ? communications.totalPages : 0}
                                </Text>
                                <Button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    isDisabled={communications?.pageable?.pageNumber === communications?.totalPages - 1}
                                    height="100%"
                                >
                                    Successiva
                                </Button>
                                <Flex alignItems="center" mx={2}>
                                    <Input
                                        type="number"
                                        placeholder="Pagina"
                                        value={inputPageNumber}
                                        onChange={(e) => setInputPageNumber(e.target.value)}
                                        width="90px"
                                        mx={2}
                                    />
                                    <Button
                                        onClick={() => {
                                            const pageNumber = parseInt(inputPageNumber, 10);
                                            if (!isNaN(pageNumber) && pageNumber > 0 && pageNumber <= communications?.totalPages) {
                                                handlePageChange(pageNumber);
                                            }
                                        }}
                                    >
                                        Vai
                                    </Button>
                                </Flex>
                            </Flex>
                        </Td>
                    </Tr>
                </Tfoot>
            </Table>
        </Card>
    );
}